import queryString from "query-string";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { styled, keyframes } from "styled-components";
import { Box, Typography, CircularProgress } from "@mui/material";

import { useAxios } from "../../hooks";
import pages from "../../constants/pages";
import { signInMember } from "../../redux/slices/memberSlice";
import { alert } from "../../redux/slices/alertSlice";

const colors = {
  growthOSLightBlue: "#97beff",
  growthOSLightDarkBlue: "#5a8bf2",
  growthOSDarkBlue: "#0234ad",
};

// Simple fade-in animation
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(
    135deg,
    ${colors.growthOSLightBlue},
    ${colors.growthOSLightDarkBlue}
  );
  animation: ${fadeIn} 0.6s ease-in-out;
`;

const Loader = styled(CircularProgress)`
  color: ${colors.growthOSDarkBlue} !important;
  margin-bottom: 20px;
`;

export default function RedirectPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const axios = useAxios({ disableAuthToken: true });

  const token = queryString.parse(window.location.search)?.token;
  const userEmail = queryString.parse(window.location.search)?.user_email;
  const agencyEmail = queryString.parse(window.location.search)?.agency_email;

  useEffect(() => {
    if (token) {
      sessionStorage.setItem("publicis_sso_token", "true");

      axios({
        url: "/sso/login",
        method: "POST",
        data: {
          token: token,
          agency_email: agencyEmail,
          user_email: userEmail,
        },
      }).then((response) => {
        if (response.status) {
          getStateData(response);
        } else {
          dispatch(
            alert({
              type: "error",
              message: response.message,
            })
          );
        }
      });
    }
  }, [axios, token, dispatch]);

  return (
    <Wrapper>
      <Loader size={50} thickness={5} />
      <Typography
        variant="h6"
        sx={{
          color: colors.growthOSDarkBlue,
          fontWeight: 600,
          letterSpacing: "0.5px",
          textAlign: "center",
        }}
      >
        You are being redirected...
      </Typography>
    </Wrapper>
  );

  async function getStateData(response) {
    const data = response.data;

    const timeStamp = new Date(data?.expiresIn).getTime();
    localStorage.setItem("expirySession", timeStamp);

    if (data.user[0]?.is_parent_brand_verified === 2) {
      dispatch(
        alert({
          type: "warning",
          message:
            "Your brand has been rejected by the admin. Please contact the admin for further details.",
        })
      );
      return;
    }
    if (data.user[0]?.role_name === "NORMAL_USER") {
      sessionStorage.setItem("ladder", JSON.stringify(data?.steps));
      sessionStorage.setItem("userID", parseInt(data.user[0]?.user_id));
      sessionStorage.setItem("authToken", data.token);
      sessionStorage.setItem(
        "businessEntityId",
        data.user[0]?.business_entity_id
      );

      sessionStorage.setItem("brandID", response.data.brands[0]?.brand_id);
      sessionStorage.setItem("role", data.user[0]?.role_name);

      if (!data.steps.step2 || !data.steps.step3) {
        navigate(pages.createWelcomePage.route);
      }

      if (data.steps.step2 && data.steps.step3) {
        if (data.user[0]?.is_parent_brand_verified === 0) {
          navigate(pages.createWelcomePage.route);
        } else if (!data.user[0]?.is_admin_verified) {
          navigate(pages.createWelcomePage.route);
        } else {
          if (data.brands[0].brand_type_id === 2) {
            sessionStorage.setItem("parentName", "Audience");
            sessionStorage.setItem("subParentName", "");

            localStorage.setItem(
              "dashboardState" + response.data.brands[0]?.brand_id,
              "monetization"
            );
          }

          if (data.brands[0].brand_type_id === 3) {
            sessionStorage.setItem("parentName", "Campaigns-request");
            sessionStorage.setItem("subParentName", "");
            localStorage.setItem(
              "dashboardState" + response.data.brands[0]?.brand_id,
              "promotion"
            );
          }

          sessionStorage.setItem(
            "activeBrand",
            response.data.brands[0]?.brand_id
          );
          sessionStorage.setItem("brandType", data.brands[0].brand_type_id);
          sessionStorage.setItem("role", "NORMAL_USER");

          await signInMember({
            authToken: data.token,
            userID: data.user[0]?.user_id,
            businessEntityId: data.user[0]?.business_entity_id,
            brandType: data.brands[0].brand_type_id,
          });

          if (data.brands[0].brand_type_id === 2) {
            navigate(pages.audienceList.route);
          }

          if (data.brands[0].brand_type_id === 3) {
            navigate(pages.campaignRequest.route);
          }
        }
      }
    }

    if (data.user[0]?.role_name === "AGENCY") {
      sessionStorage.setItem("ladder", JSON.stringify(data?.steps));
      sessionStorage.setItem("userID", parseInt(data.user[0]?.user_id));
      sessionStorage.setItem("authToken", data.token);
      sessionStorage.setItem(
        "businessEntityId",
        data.user[0]?.business_entity_id
      );

      sessionStorage.setItem("agencyID", data?.agency[0]?.agency_id);
      sessionStorage.setItem("role", data.user[0]?.role_name);

      if (!data.steps.step2 || !data.steps.step3) {
        navigate(pages.welcomeAsAgencyPage.route);
      }

      if (data.steps.step2 && data.steps.step3) {
        if (
          !data.user[0]?.is_admin_verified ||
          data.agency[0].is_agency_verified === 0
        ) {
          navigate(pages.welcomeAsAgencyPage.route);
        } else if (data.brands.length === 0) {
          navigate(pages.welcomePage.route);
        } else {
          navigate(pages.welcomePage.route);
        }
      }
    }

    if (data.user[0]?.role_name === "AGENCY_MANAGER") {
      sessionStorage.setItem("ps", "AM");
      sessionStorage.setItem("role", data.user[0]?.role_name);
      sessionStorage.setItem("userID", parseInt(data.user[0]?.user_id));
      sessionStorage.setItem("parentToken", data.token);
      sessionStorage.setItem(
        "businessEntityId",
        data.user[0]?.business_entity_id
      );

      navigate(pages.intermediatePage.route);
    }

    //create new brand or company

    dispatch(
      alert({
        type: "success",
        message: response.message.displayMessage,
      })
    );
  }
}
